import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  defaultMetaObj,
  initialStaffTargetState,
  staffTargetCompletedTodayParams,
  staffTargetInProgressParams,
  staffTargetNotStartedCurrentPendingParams,
} from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { staffTargetEndPoint } = endPoints;

export const fetchAllStaffTargets = createAsyncThunk(
  "staffTargets/fetchAllStaffTargets",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: staffTargetEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteStaffTarget = createAsyncThunk(
  "staffTargets/deleteStaffTarget",
  async ({ staffTargetId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        staffTargetEndPoint,
        staffTargetId
      );

      if (response.status === 204) {
        NotificationManager.success("Staff Target Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewStaffTarget = createAsyncThunk(
  "staffTargets/addNewStaffTarget",
  async (newStaffTarget: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${staffTargetEndPoint}/`,
        newStaffTarget
      );

      if (response.status === 201) {
        NotificationManager.success("Staff Target Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateStaffTarget = createAsyncThunk(
  "staffTargets/updateStaffTarget",
  async ({ updatedStaffTarget, staffTargetId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        staffTargetEndPoint,
        staffTargetId,
        updatedStaffTarget
      );
      if (response.status === 204) {
        NotificationManager.success("Staff Target updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllStaffTargetsForDashboard = createAsyncThunk(
  "staffTargets/fetchAllStaffTargetsForDashboard",
  async (
    { username = null }: { username?: string | null },
    { rejectWithValue }
  ) => {
    const usernameParams = username
      ? {
          assigned_user__username: username,
        }
      : {};
    const paginatedUrlCompletedTarget = getPaginatedUrlWithSearchParams({
      pageNumber: 1,

      endpoint: staffTargetEndPoint,
      searchParams: {
        ...staffTargetCompletedTodayParams,
        ...usernameParams,
      },
    });

    const paginatedUrlInProgressTarget = getPaginatedUrlWithSearchParams({
      pageNumber: 1,

      endpoint: staffTargetEndPoint,
      searchParams: {
        ...staffTargetInProgressParams,
        ...usernameParams,
      },
    });

    const paginatedUrlNotStartedTarget = getPaginatedUrlWithSearchParams({
      pageNumber: 1,

      endpoint: staffTargetEndPoint,
      searchParams: {
        ...staffTargetNotStartedCurrentPendingParams,
        ...usernameParams,
      },
    });

    try {
      const completed: AxiosResponse = await crudService.getAll(
        paginatedUrlCompletedTarget
      );
      const inprogress: AxiosResponse = await crudService.getAll(
        paginatedUrlInProgressTarget
      );
      const notStarted: AxiosResponse = await crudService.getAll(
        paginatedUrlNotStartedTarget
      );

      return [
        ...(completed?.data?.objects || []),
        ...(inprogress?.data?.objects || []),
        ...(notStarted?.data?.objects || []),
      ];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const staffTargetSlice = createSlice({
  name: "staffTargets",
  initialState: initialStaffTargetState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all staff target
    builder
      .addCase(fetchAllStaffTargets.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllStaffTargets.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.staffTargets = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllStaffTargets.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete staff target
    builder
      .addCase(deleteStaffTarget.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteStaffTarget.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteStaffTarget.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new staff target
    builder
      .addCase(addNewStaffTarget.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewStaffTarget.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewStaffTarget.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update staff target
    builder
      .addCase(updateStaffTarget.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateStaffTarget.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateStaffTarget.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    //    staff target dashboard
    builder
      .addCase(fetchAllStaffTargetsForDashboard.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllStaffTargetsForDashboard.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.staffTargetsDashboard = action.payload as any[];
        }
      )
      .addCase(
        fetchAllStaffTargetsForDashboard.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const staffTargetReducer = staffTargetSlice.reducer;
